import { isTruthy } from "@dashboard/library";
import { FEATURE_NAME } from "@/components/LicenseGuard/LicenseGuard.constant";
import { useHasLicenseAccess } from "@/components/LicenseGuard/useHasLicenseAccess";
import { SentimentType } from "@/graphql/generated/graphql";
import { pickReason } from "@/helpers/tracker/tracker.helpers";
import { useCall } from "@/hooks/useCall/useCall";
import { useCallContextRule } from "@/hooks/useCallContextRule/useCallContextRule";
import { useTranscriptionRule } from "@/hooks/useTranscriptionRules/useTranscriptionRules";
import { useBaseStore } from "@/store/store";

/**
 * Expose the shared payload field between different track events
 */
export function useTrackHelpers() {
  const { call } = useCall();
  const hasAiStandardAccess = useHasLicenseAccess({
    feature: FEATURE_NAME.AI_STANDARD_ACCESS,
  });

  const {
    shouldDisplaySentiments,
    shouldDisplayInsights,
    isTalkRatioDisplayed,
  } = useCallContextRule();
  const { isTranscriptionAvailable } = useTranscriptionRule();

  const transcriptionSettings = useBaseStore(
    (store) => store.transcriptionSettings
  );

  const isCallTranscription =
    isTruthy(call) && call.transcription?.__typename === "CallTranscription";
  const hasTopics =
    isTruthy(call) &&
    isTruthy(call.transcription) &&
    call.transcription.__typename === "CallTranscription" &&
    Boolean(call.transcription.insights?.topics);
  const hasSummary =
    isTruthy(call) &&
    isTruthy(call.transcription) &&
    call.transcription.__typename === "CallTranscription" &&
    Boolean(call.transcription.insights?.summary);
  const hasActionItems =
    isTruthy(call) &&
    isTruthy(call.transcription) &&
    call.transcription.__typename === "CallTranscription" &&
    Boolean(call.transcription.insights?.nextActions);

  const sentiment = shouldDisplaySentiments
    ? call?.overallSentiments?.get(SentimentType.EXTERNAL)
    : null;

  return {
    has_ai_add_on: hasAiStandardAccess,
    transcription_displayed: isTranscriptionAvailable,
    summary_displayed: shouldDisplayInsights && hasSummary,
    key_topics_displayed: shouldDisplayInsights && hasTopics,
    talk_ratio_displayed: isTalkRatioDisplayed,
    action_items_displayed: hasActionItems,
    sentiment_displayed: Boolean(sentiment),
    sentiment,
    reason_for_no_transcript_displayed: pickReason(
      call,
      isCallTranscription
        ? transcriptionSettings?.calls
        : transcriptionSettings?.voicemails
    ),
  };
}
